import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ANKETA_COMMENT_MAX,
  CONTACT_METHODS,
  HEARD_FROM,
  LOCALE_NAMES,
  localize,
  type AnketaSubmitResult,
  type Locale,
  type QuestionnaireDefinition,
} from '@360face/domain';
import { MESSAGES } from '../i18n/messages';
import { KInput, KeyboardProvider, KeyboardSlot } from '../kiosk/Keyboard';
import { PhoneInput } from '../kiosk/PhoneInput';
import { SignaturePad } from '../kiosk/SignaturePad';
import { AnketaApiError, type AnketaApi } from './api';
import { toE164 } from '../kiosk/phone';
import {
  emailOnFile,
  emptyDraft,
  FORM_STEPS,
  localToday,
  nextStep,
  prevStep,
  stepErrors,
  toRequest,
  type AnketaDraft,
  type AnketaStep,
  type FieldError,
} from './logic';
import { ANKETA_LOCALES, ANKETA_MESSAGES, type AnketaMessages } from './messages';
import '../v2/v2.css';
import './anketa.css';

/** No touch for this long on a half-filled form → back to the start (shared iPad). */
const IDLE_MS = 3 * 60_000;
/** The thank-you screen goes back to the start by itself. */
const DONE_MS = 25_000;

/**
 * iPad client form (/anketa, owner 18.10.2026): separate from the kiosk, 7 languages, big blocks,
 * signature. Saved on the Hub server; the client card is found or created in Altegio there.
 * Health: the client reads the contraindications and answers only "none" / "something applies".
 */
export function Anketa({ api, locationId }: { api: AnketaApi; locationId: string }) {
  const [locale, setLocale] = useState<Locale>('pt');
  const [step, setStep] = useState<AnketaStep>('welcome');
  const [draft, setDraft] = useState<AnketaDraft>(emptyDraft);
  const [shown, setShown] = useState<Record<string, FieldError>>({});
  const [def, setDef] = useState<QuestionnaireDefinition>();
  const [defError, setDefError] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<keyof AnketaMessages>();
  const [result, setResult] = useState<AnketaSubmitResult>();
  const [langOpen, setLangOpen] = useState(false);
  const [sigKey, setSigKey] = useState(0);
  const m = ANKETA_MESSAGES[locale];
  const top = useRef<HTMLElement>(null);

  const loadDef = useCallback(() => {
    setDefError(false);
    api.definition(locationId).then(setDef, () => setDefError(true));
  }, [api, locationId]);
  useEffect(loadDef, [loadDef]);

  const reset = useCallback(() => {
    setStep('welcome');
    setDraft(emptyDraft());
    setShown({});
    setSendError(undefined);
    setResult(undefined);
    setLangOpen(false);
    setSigKey((k) => k + 1);
    setLocale('pt');
    loadDef();
  }, [loadDef]);

  // idle reset on a half-filled form; the thank-you screen resets by itself
  useEffect(() => {
    if (step === 'welcome') return;
    const ms = step === 'done' ? DONE_MS : IDLE_MS;
    let t = window.setTimeout(reset, ms);
    const touch = () => {
      window.clearTimeout(t);
      t = window.setTimeout(reset, ms);
    };
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
    };
  }, [step, reset]);

  useEffect(() => {
    top.current?.scrollTo?.({ top: 0 });
  }, [step]);

  const set = <K extends keyof AnketaDraft>(k: K, v: AnketaDraft[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    // the error shown for this field (day / month / year share "birth"; both consents share "consent")
    const key =
      k === 'day' || k === 'month' || k === 'year'
        ? 'birth'
        : k === 'privacy'
          ? 'consent'
          : k === 'country'
            ? 'phone'
            : k;
    setShown((s) => {
      const { [key]: _gone, ...rest } = s;
      void _gone;
      return rest;
    });
  };

  const today = localToday();
  const goNext = async () => {
    const errs = stepErrors(step, draft, today);
    if (Object.keys(errs).length > 0) {
      setShown(errs);
      return;
    }
    setShown({});
    // «Is this you?» (owner 28.10.2026): first the phone is looked up, then the rest is asked
    if (step === 'details' && draft.match === undefined) {
      const phone = toE164(draft.country, draft.phone);
      if (!phone) return;
      setSending(true);
      const found = await api.recognize(locationId, phone).catch(() => undefined);
      setSending(false);
      setDraft((d) =>
        found?.known && found.token && found.name
          ? {
              ...d,
              match: {
                token: found.token,
                name: found.name,
                ...(found.emailMask ? { emailMask: found.emailMask } : {}),
              },
            }
          : { ...d, match: 'none' },
      );
      return;
    }
    if (step === 'details' && draft.match !== 'none' && !draft.confirmed) return;
    if (step !== 'sign') {
      setStep(nextStep(step));
      return;
    }
    if (!def || sending) return;
    setSending(true);
    setSendError(undefined);
    try {
      setResult(await api.submit(locationId, toRequest(draft, def.version, locale, today)));
      setStep('done');
    } catch (e) {
      const code = e instanceof AnketaApiError ? e.code : 'UNKNOWN';
      if (code === 'QUESTIONNAIRE_VERSION_MISMATCH') loadDef();
      setSendError(
        code === 'NETWORK'
          ? 'errNetwork'
          : code === 'QUESTIONNAIRE_VERSION_MISMATCH'
            ? 'errVersion'
            : code === 'TOO_MANY_REQUESTS'
              ? 'errBusy'
              : code === 'QUESTIONNAIRE_INCOMPLETE'
                ? 'errBirth'
                : 'errGeneric',
      );
    } finally {
      setSending(false);
    }
  };

  if (step === 'welcome') {
    return (
      <div className="v2 v2-kiosk an an-welcome" lang={locale}>
        <div className="an-welcome-mark">
          360<span>°</span>FACE
        </div>
        <p className="an-welcome-title">{m.title}</p>
        <div className="an-langs">
          {ANKETA_LOCALES.map((l) => (
            <button
              key={l}
              lang={l}
              className="v2-btn v2-btn-light"
              disabled={!def}
              onClick={() => {
                setLocale(l);
                setStep('details');
              }}
            >
              {LOCALE_NAMES[l]}
            </button>
          ))}
        </div>
        {defError && (
          <button className="v2-btn v2-btn-light an-retry" onClick={loadDef}>
            {m.errNetwork}
          </button>
        )}
      </div>
    );
  }

  const err = (k: string) => (shown[k] ? m[shown[k]!] : undefined);
  const stepIndex = FORM_STEPS.indexOf(step);

  const kbLabels = { done: MESSAGES[locale].kbDone, space: MESSAGES[locale].kbSpace };
  // Owner 03.11.2026: the kiosk's own big keyboard instead of the iPad one — a digit keypad right
  // under the phone, letters / e-mail / digits at the bottom for the other fields.
  return (
    <KeyboardProvider>
      <div className="v2 v2-kiosk an" lang={locale}>
        <header className="v2-top">
          <button className="v2-logo v2-logo-type" onClick={reset} aria-label="360°FACE">
            360<i>°</i>FACE
          </button>
          <span className="v2-spacer" />
          {step !== 'done' && (
            <button className="v2-chip-btn" aria-haspopup="dialog" onClick={() => setLangOpen(true)}>
              {LOCALE_NAMES[locale]}
            </button>
          )}
        </header>

        {stepIndex >= 0 && (
          <ol className="v2-steps an-steps" aria-label={m.title}>
            {m.steps.map((label, i) => (
              <li key={label} className={i < stepIndex ? 'done' : i === stepIndex ? 'now' : undefined}>
                {label}
              </li>
            ))}
          </ol>
        )}

        <main className="v2-body" ref={top}>
          <div className="v2-stack an-stack">
            {def?.draft && step !== 'done' && <p className="an-draft">{m.draft}</p>}

            {step === 'details' && (
              <>
                <h1 className="v2-h1">{m.detailsTitle}</h1>
                {/* Same country picker as the kiosk: Portugal first, top countries, search (owner 19.10.2026) */}
                <div className="v2-field an-field">
                  <span>
                    {m.phone} <small className="an-opt">· {`${m.required} · ${m.phoneHint}`}</small>
                  </span>
                  <PhoneInput
                    big
                    country={draft.country}
                    value={draft.phone}
                    invalid={!!err('phone')}
                    label={m.phone}
                    locale={locale}
                    m={MESSAGES[locale]}
                    onChange={(p) => {
                      if (p.country) set('country', p.country);
                      if (p.phone !== undefined) set('phone', p.phone);
                      setDraft((d) => ({ ...d, match: undefined, confirmed: false, rejected: false }));
                    }}
                  />
                  {err('phone') && <em role="alert">{err('phone')}</em>}
                  {draft.match === undefined && <small className="an-opt">{m.phoneBooking}</small>}
                </div>
                <KeyboardSlot labels={kbLabels} inline />
                {draft.match && draft.match !== 'none' && !draft.confirmed && (
                  <section className="v2-card an-match" aria-live="polite">
                    <h2 className="v2-h2">
                      {m.isThisYou} <b>{draft.match.name}</b>
                    </h2>
                    {draft.rejected ? (
                      <>
                        <p className="an-note">{m.notMeHint}</p>
                        <button
                          type="button"
                          className="v2-btn"
                          onClick={() =>
                            setDraft((d) => ({ ...d, phone: '', match: undefined, confirmed: false, rejected: false }))
                          }
                        >
                          {m.otherNumber}
                        </button>
                      </>
                    ) : (
                      <div className="v2-pair">
                        <button type="button" className="v2-btn red" onClick={() => set('confirmed', true)}>
                          {m.yesMe}
                        </button>
                        <button type="button" className="v2-btn" onClick={() => set('rejected', true)}>
                          {m.notMe}
                        </button>
                      </div>
                    )}
                  </section>
                )}
                {draft.confirmed && draft.match && draft.match !== 'none' && (
                  <p className="an-known">✓ {draft.match.name}</p>
                )}
                {(draft.match === 'none' || draft.confirmed) && (
                  <>
                    {draft.match === 'none' && (
                      <div className="v2-pair">
                        <Field label={m.firstName} hint={m.required} error={err('firstName')}>
                          <KInput
                            mode="text"
                            className={err('firstName') ? 'v2-input invalid' : 'v2-input'}
                            max={40}
                            value={draft.firstName}
                            onValue={(v) => set('firstName', v)}
                          />
                        </Field>
                        <Field label={m.lastName} hint={m.required} error={err('lastName')}>
                          <KInput
                            mode="text"
                            className={err('lastName') ? 'v2-input invalid' : 'v2-input'}
                            max={60}
                            value={draft.lastName}
                            onValue={(v) => set('lastName', v)}
                          />
                        </Field>
                      </div>
                    )}
                    <Field label={m.birth} hint={m.required} error={err('birth')}>
                      <div className="an-birth">
                        {(['day', 'month', 'year'] as const).map((k) => (
                          <KInput
                            key={k}
                            mode="digits"
                            className={err('birth') ? 'v2-input invalid' : 'v2-input'}
                            aria-label={m[k]}
                            placeholder={m[k]}
                            max={k === 'year' ? 4 : 2}
                            value={draft[k]}
                            onValue={(v) => set(k, v.replace(/\D/g, ''))}
                          />
                        ))}
                      </div>
                    </Field>
                    {emailOnFile(draft) && draft.match && draft.match !== 'none' ? (
                      <p className="an-known">
                        {m.emailOnFile}: {draft.match.emailMask}
                      </p>
                    ) : (
                      <Field label={m.email} hint={`${m.optional} · ${m.emailHint}`} error={err('email')}>
                        <KInput
                          mode="email"
                          className={err('email') ? 'v2-input invalid' : 'v2-input'}
                          autoCapitalize="none"
                          max={120}
                          value={draft.email}
                          onValue={(v) => set('email', v)}
                        />
                      </Field>
                    )}
                  </>
                )}
              </>
            )}

            {step === 'prefs' && (
              <>
                <h1 className="v2-h1">{m.prefsTitle}</h1>
                <section className="v2-card">
                  <h2 className="v2-h2">
                    {m.pressure} <small className="an-opt">· {m.optional}</small>
                  </h2>
                  <div className="an-scale" role="radiogroup" aria-label={m.pressure}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        role="radio"
                        aria-checked={draft.pressure === n}
                        className={draft.pressure === n ? 'on' : undefined}
                        onClick={() => set('pressure', draft.pressure === n ? undefined : n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="an-scale-ends">
                    <span>{m.pressureSoft}</span>
                    <span>{m.pressureStrong}</span>
                  </div>
                </section>
                <Chips
                  title={m.contact}
                  optional={m.optional}
                  options={CONTACT_METHODS.map((c) => [c, m.contactOptions[c]] as const)}
                  value={draft.contact}
                  onChange={(v) => set('contact', v)}
                />
                <Chips
                  title={m.heard}
                  optional={m.optional}
                  options={HEARD_FROM.map((h) => [h, m.heardOptions[h]] as const)}
                  value={draft.heardFrom}
                  onChange={(v) => set('heardFrom', v)}
                />
              </>
            )}

            {step === 'health' && def && (
              <>
                <h1 className="v2-h1">{m.healthTitle}</h1>
                <p className="v2-lead">{def.simple ? localize(def.intro, locale) : m.healthIntro}</p>
                <section className="v2-card">
                  {def.conditionsTitle && <h2 className="v2-h2">{localize(def.conditionsTitle, locale)}</h2>}
                  <ul className="an-conditions">
                    {def.conditions.map((c) => (
                      <li key={c.id}>{localize(c.text, locale)}</li>
                    ))}
                  </ul>
                  {def.notes?.map((n, i) => (
                    <p key={i} className="an-note">
                      {localize(n, locale)}
                    </p>
                  ))}
                </section>
                {def.allergies && (
                  <section className="v2-card">
                    <h2 className="v2-h2">{localize(def.allergies.title, locale)}</h2>
                    <ul className="an-conditions">
                      {def.allergies.items.map((c) => (
                        <li key={c.id}>{localize(c.text, locale)}</li>
                      ))}
                    </ul>
                  </section>
                )}
                {/* v4 (owner 19.10.2026): no questions — one confirmation; only "confirmed" is stored */}
                <section className="v2-card">
                  <Check
                    on={draft.conditions === 'none' && draft.aesthetic === 'none'}
                    onClick={() => {
                      const on = !(draft.conditions === 'none' && draft.aesthetic === 'none');
                      set('conditions', on ? 'none' : undefined);
                      set('aesthetic', on ? 'none' : undefined);
                    }}
                  >
                    <b>{def.confirm ? localize(def.confirm, locale) : m.conditionsNone}</b>
                  </Check>
                  {err('conditions') && <p className="v2-err">{err('conditions')}</p>}
                </section>
                {/* owner 28.10.2026: the client may leave a comment; it goes to her card Note in Altegio */}
                <label className="v2-field v2-comment an-comment">
                  <span>{m.commentLabel}</span>
                  <KInput
                    mode="text"
                    multiline
                    className="v2-input"
                    rows={3}
                    max={ANKETA_COMMENT_MAX}
                    placeholder={m.commentHint}
                    value={draft.comment}
                    onValue={(v) => set('comment', v)}
                  />
                </label>
              </>
            )}

            {step === 'sign' && (
              <>
                <h1 className="v2-h1">{m.signTitle}</h1>
                <section className="v2-card">
                  <Check on={draft.consent} onClick={() => set('consent', !draft.consent)}>
                    {m.consent}
                  </Check>
                  <Check on={draft.privacy} onClick={() => set('privacy', !draft.privacy)}>
                    {m.privacy}
                  </Check>
                  <Check on={draft.marketing} onClick={() => set('marketing', !draft.marketing)}>
                    {m.marketing}
                  </Check>
                  {err('consent') && <p className="v2-err">{err('consent')}</p>}
                </section>
                <section className="v2-card an-sign">
                  <SignaturePad
                    key={sigKey}
                    label={m.signLabel}
                    clearLabel={m.clear}
                    onChange={(png) => set('signature', png)}
                  />
                  {err('signature') && <p className="v2-err">{err('signature')}</p>}
                </section>
              </>
            )}

            {step === 'done' && result && (
              <div className="v2-center an-done" role="status">
                <span className="v2-done-mark" aria-hidden>
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h1 className="v2-h1">{m.thanks(result.firstName)}</h1>
                <p className="v2-lead">{m.saved}</p>
                {result.tellTherapist && <p className="v2-notice">{m.tellTherapist}</p>}
                <p className="v2-note">{m.handBack}</p>
              </div>
            )}
          </div>
        </main>

        <KeyboardSlot labels={kbLabels} skipInline />

        <footer className="v2-rail">
          {sendError && (
            <p className="an-send-err" role="alert">
              {m[sendError] as string}
            </p>
          )}
          <div className="v2-rail-row">
            {step === 'done' ? (
              <>
                <span />
                <div className="v2-rail-action">
                  <button className="v2-btn v2-btn-red" onClick={reset}>
                    {m.newForm}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="v2-rail-left">
                  <button className="v2-btn v2-rail-back" onClick={() => setStep(prevStep(step))} disabled={sending}>
                    {m.back}
                  </button>
                </div>
                <div className="v2-rail-action">
                  <button
                    className="v2-btn v2-btn-red"
                    onClick={() => void goNext()}
                    disabled={sending || (step === 'health' && !def)}
                  >
                    {step === 'sign' ? (sending ? m.sending : m.submit) : m.next}
                  </button>
                </div>
              </>
            )}
          </div>
        </footer>

        {langOpen && (
          <div className="v2-backdrop" onClick={() => setLangOpen(false)}>
            <div className="v2-sheet" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
              <div className="an-langs an-langs-sheet">
                {ANKETA_LOCALES.map((l) => (
                  <button
                    key={l}
                    lang={l}
                    className={l === locale ? 'v2-btn v2-btn-ink' : 'v2-btn v2-btn-quiet'}
                    onClick={() => {
                      setLocale(l);
                      setLangOpen(false);
                    }}
                  >
                    {LOCALE_NAMES[l]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </KeyboardProvider>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="v2-field an-field">
      <span>
        {label} {hint && <small className="an-opt">· {hint}</small>}
      </span>
      {children}
      {error && <em role="alert">{error}</em>}
    </label>
  );
}

function Check({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button role="checkbox" aria-checked={on} className="v2-check an-check" onClick={onClick}>
      <span className="v2-box" aria-hidden>
        {on ? '✓' : ''}
      </span>
      <span>{children}</span>
    </button>
  );
}

function Chips<T extends string>({
  title,
  optional,
  options,
  value,
  onChange,
}: {
  title: string;
  optional: string;
  options: ReadonlyArray<readonly [T, string]>;
  value?: T;
  onChange: (v: T | undefined) => void;
}) {
  const id = useMemo(() => `an-${Math.random().toString(36).slice(2)}`, []);
  return (
    <section className="v2-card">
      <h2 className="v2-h2" id={id}>
        {title} <small className="an-opt">· {optional}</small>
      </h2>
      <div className="an-chips" role="radiogroup" aria-labelledby={id}>
        {options.map(([k, label]) => (
          <button
            key={k}
            role="radio"
            aria-checked={value === k}
            className={value === k ? 'on' : undefined}
            onClick={() => onChange(value === k ? undefined : k)}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
}
